/**
 * Clarix — Invoice Verification Report  (Milestone 11 Phase C)
 *
 * Route: /projects/:id/verifications/:vid
 *
 * Shows the full result of an invoice vs offer verification:
 *   - KPI strip (invoice total, offer total, variance, flags)
 *   - AI narrative summary
 *   - Line comparison table (critical → warning → ok order)
 *   - Approve / Dispute status controls
 *   - Re-run button (bypasses cache, calls Claude again)
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// ─── DESIGN TOKENS (matches ProjectDashboard) ──────────────────────────────────

const V = {
  ink:      '#0c1830',
  ink2:     '#2a3956',
  ink3:     '#5a6680',
  ink4:     '#8a93a6',
  paper:    '#fbfaf6',
  paper2:   '#f3f1ea',
  paper3:   '#e6e2d5',
  line:     '#d8d3c2',
  gold:     '#c8a64b',
  good:     '#2a6f4f',
  goodSoft: '#e8f0eb',
  bad:      '#9c2b1a',
  badSoft:  '#f4e8e5',
  warn:     '#a8731a',
  warnSoft: '#f5ecd9',
  serif:    "'Source Serif 4', Georgia, serif",
  sans:     "'Inter', -apple-system, system-ui, sans-serif",
  mono:     "'JetBrains Mono', ui-monospace, monospace",
};

// ─── FONT LOADER ──────────────────────────────────────────────────────────────

function FontLoader() {
  useEffect(() => {
    if (document.getElementById('verdict-fonts')) return;
    const link = document.createElement('link');
    link.id   = 'verdict-fonts';
    link.href = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,300;8..60,400;8..60,500;8..60,600;8..60,700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';
    link.rel  = 'stylesheet';
    document.head.appendChild(link);
  }, []);
  return null;
}

// ─── NAV (shared with ProjectDashboard) ────────────────────────────────────────

function UtilBar({ label }) {
  return (
    <div style={{ background: V.ink, height: 32, display: 'flex', alignItems: 'center', padding: '0 24px', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 16, borderRight: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: V.gold }}></div>
      </div>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, color: V.gold, textTransform: 'uppercase' }}>INVOICE VERIFICATION</div>
      <div style={{ fontSize: 11, color: 'rgba(251,250,246,0.55)', fontFamily: V.sans }}>{label}</div>
    </div>
  );
}

function TopNav({ user, logout, navigate }) {
  return (
    <div style={{ background: V.paper, borderBottom: `1px solid ${V.line}`, height: 60, display: 'flex', alignItems: 'center', padding: '0 24px', gap: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', fontFamily: V.serif, fontSize: 26, fontWeight: 600, letterSpacing: -0.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
        <span style={{ color: V.gold }}>V</span>
        <span style={{ color: V.ink }}>erdict</span>
      </div>
      <div style={{ width: 1, height: 18, background: V.line, margin: '0 12px' }}></div>
      <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: 2.5, textTransform: 'uppercase', color: V.ink3, marginRight: 32 }}>Negotiation Intelligence</div>
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
        {user && <span style={{ fontSize: 12, color: V.ink4 }}>{user.email}</span>}
        {logout && <button style={{ background: 'none', border: 'none', fontSize: 12, color: V.ink3, cursor: 'pointer', fontFamily: V.sans }} onClick={logout}>Sign out</button>}
      </div>
    </div>
  );
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────

function fv(v, cur = 'EUR') {
  if (v == null) return '—';
  const sym = cur === 'USD' ? '$' : cur === 'GBP' ? '£' : '€';
  return `${sym}${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pct(v) {
  if (v == null) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${Number(v).toFixed(2)}%`;
}

function fd(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Flag configuration
const FLAG = {
  critical: { label: 'Critical', color: V.bad,  bg: V.badSoft,  icon: '⚠' },
  warning:  { label: 'Warning',  color: V.warn, bg: V.warnSoft, icon: '△' },
  ok:       { label: 'OK',       color: V.good, bg: V.goodSoft, icon: '✓' },
};

// Match type labels
const MATCH_TYPE = {
  matched:      { label: 'Matched',      color: V.ink3,  bg: V.paper3  },
  extra_item:   { label: 'Extra item',   color: V.bad,   bg: V.badSoft },
  missing_item: { label: 'Missing',      color: V.warn,  bg: V.warnSoft},
};

// Overall status configuration
const STATUS_CFG = {
  verified:            { label: 'Verified — No Flags',       color: V.good, bg: V.goodSoft },
  discrepancies_found: { label: 'Discrepancies Found',       color: V.bad,  bg: V.badSoft  },
  approved:            { label: 'Approved',                  color: V.good, bg: V.goodSoft },
  disputed:            { label: 'Disputed',                  color: V.bad,  bg: V.badSoft  },
};

// ─── FLAG BADGE ───────────────────────────────────────────────────────────────

function FlagBadge({ level }) {
  const cfg = FLAG[level] || FLAG.ok;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontFamily: V.mono, fontSize: 9, fontWeight: 600, letterSpacing: 0.8,
      padding: '3px 8px', borderRadius: 3, textTransform: 'uppercase',
      color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap',
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

// ─── MATCH TYPE BADGE ─────────────────────────────────────────────────────────

function MatchBadge({ type }) {
  const cfg = MATCH_TYPE[type] || { label: type, color: V.ink3, bg: V.paper2 };
  return (
    <span style={{
      fontFamily: V.mono, fontSize: 9, fontWeight: 500, letterSpacing: 0.5,
      padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase',
      color: cfg.color, background: cfg.bg,
    }}>
      {cfg.label}
    </span>
  );
}

// ─── LINE COMPARISON TABLE ────────────────────────────────────────────────────

function LineTable({ lines }) {
  const [expanded, setExpanded] = useState(null);

  if (!lines || lines.length === 0) {
    return <div style={{ fontSize: 13, color: V.ink4, fontFamily: V.sans }}>No line comparisons available.</div>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: V.sans }}>
        <thead>
          <tr style={{ background: V.paper2 }}>
            {['Flag', 'Type', 'Invoice item', 'Offer item', 'Inv. unit price', 'Offer unit price', 'Qty', 'Variance', 'Conf.'].map(h => (
              <th key={h} style={{
                fontSize: 9, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase',
                color: V.ink4, padding: '10px 12px', textAlign: 'left',
                borderBottom: `1px solid ${V.line}`, whiteSpace: 'nowrap',
              }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map((line, i) => {
            const isOpen     = expanded === i;
            const flagCfg    = FLAG[line.flag_level] || FLAG.ok;
            const varAmt     = line.price_variance_pct;
            const varColor   = varAmt == null ? V.ink4 : varAmt > 5 ? V.bad : varAmt > 0.5 ? V.warn : V.good;
            const rowBg      = line.flag_level === 'critical' ? '#fdf5f4' : line.flag_level === 'warning' ? '#fdf9f2' : V.paper;

            return (
              <React.Fragment key={i}>
                <tr
                  style={{ background: rowBg, borderBottom: `1px solid ${V.line}`, cursor: 'pointer', transition: 'background 0.1s' }}
                  onClick={() => setExpanded(isOpen ? null : i)}
                  onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = V.paper2; }}
                  onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}
                >
                  {/* Flag */}
                  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                    <FlagBadge level={line.flag_level} />
                  </td>

                  {/* Match type */}
                  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                    <MatchBadge type={line.match_type} />
                  </td>

                  {/* Invoice description */}
                  <td style={{ padding: '10px 12px', verticalAlign: 'middle', maxWidth: 220 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: line.invoice_description ? V.ink : V.ink4, fontFamily: V.mono, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {line.invoice_description || <em style={{ fontStyle: 'italic', fontFamily: V.sans }}>Not on invoice</em>}
                    </div>
                    {line.invoice_quantity && (
                      <div style={{ fontSize: 10, color: V.ink4, marginTop: 2 }}>
                        {line.invoice_quantity} {line.invoice_unit || ''}
                      </div>
                    )}
                  </td>

                  {/* Offer description */}
                  <td style={{ padding: '10px 12px', verticalAlign: 'middle', maxWidth: 220 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: line.offer_description ? V.ink2 : V.ink4, fontFamily: V.mono, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {line.offer_description || <em style={{ fontStyle: 'italic', fontFamily: V.sans }}>Not on offer</em>}
                    </div>
                  </td>

                  {/* Invoice unit price */}
                  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                    <span style={{ fontFamily: V.mono, fontSize: 12, color: V.ink }}>
                      {line.invoice_unit_price != null ? `${line.invoice_currency || 'EUR'} ${Number(line.invoice_unit_price).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '—'}
                    </span>
                  </td>

                  {/* Offer unit price */}
                  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                    <span style={{ fontFamily: V.mono, fontSize: 12, color: V.ink3 }}>
                      {line.offer_unit_price != null ? `${Number(line.offer_unit_price).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '—'}
                    </span>
                  </td>

                  {/* Qty */}
                  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                    <span style={{ fontFamily: V.mono, fontSize: 12, color: V.ink3 }}>
                      {line.invoice_quantity ?? '—'}
                    </span>
                  </td>

                  {/* Variance % */}
                  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                    <span style={{ fontFamily: V.mono, fontSize: 12, fontWeight: 600, color: varColor }}>
                      {varAmt != null ? pct(varAmt) : '—'}
                    </span>
                  </td>

                  {/* Confidence */}
                  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                    <span style={{ fontFamily: V.mono, fontSize: 12, color: V.ink4 }}>
                      {line.match_confidence != null ? `${line.match_confidence}%` : '—'}
                    </span>
                  </td>
                </tr>

                {/* Expanded note row */}
                {isOpen && line.match_note && (
                  <tr style={{ background: flagCfg.bg }}>
                    <td colSpan={9} style={{ padding: '8px 14px 12px 14px', fontSize: 12, color: flagCfg.color, fontFamily: V.sans, fontStyle: 'italic', borderBottom: `1px solid ${V.line}` }}>
                      {flagCfg.icon} {line.match_note}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function InvoiceVerification() {
  const { id, vid } = useParams();
  const navigate    = useNavigate();
  const { user, logout } = useAuth();

  const [data, setData]           = useState(null);      // { verification, line_comparisons, summary_counts }
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [rerunning, setRerunning] = useState(false);
  const [updating, setUpdating]   = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(`/api/verifications/${vid}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load verification.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [vid]);

  const handleRerun = async () => {
    if (!data) return;
    const { verification } = data;
    const confirm = window.confirm('Re-run this verification? Claude will re-analyse both documents. This will replace the existing result.');
    if (!confirm) return;

    setRerunning(true);
    try {
      const res = await api.post('/api/verifications', {
        invoice_document_id: verification.invoice_document_id,
        offer_document_id:   verification.offer_document_id,
        force:               true,
      });
      // Navigate to the new verification (may be same ID if existing was updated, or new ID)
      const newVid = res.data.verification.id;
      if (String(newVid) !== String(vid)) {
        navigate(`/projects/${id}/verifications/${newVid}`);
      } else {
        load(); // Same ID — just reload
      }
    } catch (e) {
      alert(e.response?.data?.error || 'Re-run failed. Please try again.');
    } finally { setRerunning(false); }
  };

  const handleStatus = async (status) => {
    setUpdating(true);
    try {
      await api.put(`/api/verifications/${vid}/status`, { status });
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Status update failed.');
    } finally { setUpdating(false); }
  };

  // ── Loading / error states ──────────────────────────────────────────────────

  if (loading) return (
    <div style={{ minHeight: '100vh', background: V.paper2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: V.sans, color: V.ink4 }}>
      Loading verification…
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: V.paper2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: V.sans, color: V.bad }}>
      {error}
    </div>
  );

  const { verification: v, line_comparisons: lines, summary_counts: counts } = data;
  const statusCfg = STATUS_CFG[v.status] || { label: v.status, color: V.ink3, bg: V.paper3 };

  const varianceColor = v.variance_pct == null ? V.ink4
    : Math.abs(v.variance_pct) > 5   ? V.bad
    : Math.abs(v.variance_pct) > 0.5 ? V.warn
    : V.good;

  return (
    <div style={{ minHeight: '100vh', background: V.paper2, fontFamily: V.sans, color: V.ink }}>
      <FontLoader />

      <UtilBar label={`${v.invoice_doc_name} vs ${v.offer_doc_name}`} />
      <TopNav user={user} logout={logout} navigate={navigate} />

      {/* Breadcrumb */}
      <div style={{ background: V.paper, borderBottom: `1px solid ${V.line}`, padding: '0 48px', height: 52, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 13, color: V.ink4, cursor: 'pointer', fontFamily: V.sans }} onClick={() => navigate('/')}>Negotiations</span>
        <span style={{ fontSize: 13, color: V.line }}>/</span>
        <span style={{ fontSize: 13, color: V.ink4, cursor: 'pointer', fontFamily: V.sans }} onClick={() => navigate(`/projects/${id}`)}>Project</span>
        <span style={{ fontSize: 13, color: V.line }}>/</span>
        <span style={{ fontSize: 13, color: V.ink, fontWeight: 500 }}>Invoice Verification #{v.id}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Re-run */}
          <button
            style={{ background: 'none', border: `1px solid ${V.line}`, borderRadius: 4, padding: '6px 14px', fontSize: 12, fontWeight: 500, color: V.ink3, cursor: 'pointer', fontFamily: V.sans, opacity: rerunning ? 0.5 : 1 }}
            onClick={handleRerun}
            disabled={rerunning || updating}
          >
            {rerunning ? 'Re-running…' : '↺ Re-run'}
          </button>
          {/* Dispute */}
          {v.status !== 'disputed' && (
            <button
              style={{ background: V.badSoft, border: `1px solid ${V.bad}`, borderRadius: 4, padding: '6px 14px', fontSize: 12, fontWeight: 600, color: V.bad, cursor: 'pointer', fontFamily: V.sans, opacity: updating ? 0.5 : 1 }}
              onClick={() => handleStatus('disputed')}
              disabled={updating || rerunning}
            >
              Dispute
            </button>
          )}
          {/* Approve */}
          {v.status !== 'approved' && (
            <button
              style={{ background: V.goodSoft, border: `1px solid ${V.good}`, borderRadius: 4, padding: '6px 14px', fontSize: 12, fontWeight: 600, color: V.good, cursor: 'pointer', fontFamily: V.sans, opacity: updating ? 0.5 : 1 }}
              onClick={() => handleStatus('approved')}
              disabled={updating || rerunning}
            >
              Approve
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '36px 48px 80px', maxWidth: 1440, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 28 }}>

        {/* Page heading */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: 24, borderBottom: `1px solid ${V.line}` }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: V.ink3, marginBottom: 10 }}>
              <span style={{ fontFamily: V.mono, fontSize: 9, fontWeight: 500, letterSpacing: 1, padding: '3px 8px', borderRadius: 3, background: V.paper3, color: V.ink3 }}>INVOICE VERIFICATION</span>
              <span style={{ color: V.line }}>·</span>
              <span>{v.supplier_name || v.supplier || 'Unknown supplier'}</span>
              <span style={{ color: V.line }}>·</span>
              <span>{fd(v.verified_at)}</span>
              {data.cached && (
                <>
                  <span style={{ color: V.line }}>·</span>
                  <span style={{ fontFamily: V.mono, fontSize: 9, padding: '2px 6px', borderRadius: 3, background: '#dbeafe', color: '#1a4d8f' }}>CACHED</span>
                </>
              )}
            </div>
            <div style={{ fontFamily: V.serif, fontSize: 28, fontWeight: 400, color: V.ink, lineHeight: 1.2, letterSpacing: -0.5, marginBottom: 6 }}>
              {v.invoice_doc_name}
            </div>
            <div style={{ fontSize: 13, color: V.ink4 }}>
              vs <strong style={{ color: V.ink2 }}>{v.offer_doc_name}</strong>
            </div>
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 4, fontFamily: V.sans, letterSpacing: 0.3, color: statusCfg.color, background: statusCfg.bg, whiteSpace: 'nowrap' }}>
            {statusCfg.label}
          </span>
        </div>

        {/* KPI strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, background: V.line, border: `1px solid ${V.line}`, borderRadius: 4, overflow: 'hidden' }}>
          {[
            { label: 'INVOICE TOTAL',  value: fv(v.total_invoice_value, 'EUR'), accent: false },
            { label: 'OFFER TOTAL',    value: fv(v.total_offer_value, 'EUR'),   accent: false },
            { label: 'VARIANCE',       value: fv(v.variance_amount, 'EUR'),     accent: true, color: varianceColor },
            { label: 'VARIANCE %',     value: pct(v.variance_pct),             accent: true, color: varianceColor },
            { label: 'FLAGS',          value: counts.critical + counts.warning, accent: counts.critical + counts.warning > 0, color: counts.critical > 0 ? V.bad : V.warn },
          ].map((k, i) => (
            <div key={i} style={{ background: V.paper, padding: '20px 24px', borderTop: k.accent && (counts?.critical > 0 || k.label === 'VARIANCE') ? `2px solid ${k.color || V.gold}` : `2px solid ${V.line}` }}>
              <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: 2, color: V.ink4, marginBottom: 10, textTransform: 'uppercase' }}>{k.label}</div>
              <div style={{ fontFamily: V.mono, fontSize: 26, fontWeight: 500, lineHeight: 1, letterSpacing: -1, color: k.color || V.ink }}>{k.value}</div>
              {k.label === 'FLAGS' && (
                <div style={{ fontSize: 11, color: V.ink4, marginTop: 6 }}>
                  {counts.critical} critical · {counts.warning} warning
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Summary counts bar */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {[
            { label: `${counts.matched} matched`,   color: V.ink3,  bg: V.paper3  },
            { label: `${counts.ok} OK`,             color: V.good,  bg: V.goodSoft },
            { label: `${counts.warning} warning`,   color: V.warn,  bg: V.warnSoft },
            { label: `${counts.critical} critical`, color: V.bad,   bg: V.badSoft  },
            { label: `${counts.total} total lines`, color: V.ink4,  bg: V.paper2   },
          ].map((b, i) => (
            <span key={i} style={{ fontFamily: V.mono, fontSize: 10, fontWeight: 600, padding: '4px 10px', borderRadius: 3, color: b.color, background: b.bg, letterSpacing: 0.5 }}>
              {b.label}
            </span>
          ))}
        </div>

        {/* AI Summary */}
        {v.ai_summary && (
          <div style={{ background: V.paper, border: `0.5px solid ${V.line}`, borderLeft: `3px solid ${V.gold}`, borderRadius: 6, padding: '20px 24px' }}>
            <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: 2, color: V.gold, marginBottom: 10, textTransform: 'uppercase' }}>AI Summary</div>
            <div style={{ fontSize: 14, color: V.ink2, fontFamily: V.serif, lineHeight: 1.7, letterSpacing: 0.1 }}>
              {v.ai_summary}
            </div>
          </div>
        )}

        {/* Line comparison table */}
        <div style={{ background: V.paper, border: `0.5px solid ${V.line}`, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: '18px 24px', borderBottom: `1px solid ${V.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: V.serif, fontSize: 16, fontWeight: 500, color: V.ink }}>Line Item Comparison</div>
            <div style={{ fontSize: 11, color: V.ink4, fontFamily: V.sans }}>Click any row to see match notes</div>
          </div>
          <div style={{ padding: '0 0 8px' }}>
            <LineTable lines={lines} />
          </div>
        </div>

        {/* Documents reference */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          {[
            { role: 'Invoice', id: v.invoice_document_id, name: v.invoice_doc_name },
            { role: 'Accepted Offer', id: v.offer_document_id, name: v.offer_doc_name },
          ].map(doc => (
            <div key={doc.role} style={{ background: V.paper, border: `0.5px solid ${V.line}`, borderRadius: 6, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: 1.5, color: V.ink4, textTransform: 'uppercase', marginBottom: 4 }}>{doc.role}</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: V.ink, fontFamily: V.mono }}>{doc.name}</div>
              </div>
              <span
                style={{ fontSize: 12, color: V.gold, cursor: 'pointer', fontFamily: V.sans }}
                onClick={() => navigate(`/projects/${id}/documents`)}
              >
                View in library →
              </span>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
